import React, { useMemo, useState } from 'react'
import { Col, Container, Row, Stack } from 'react-bootstrap'

import { createState, IntegrationServiceId } from '../../../../common/integrations'

import { useDeleteUserIntegrationMutation, useGetMeQuery } from '../../../../store/user/user'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../store/hooks'

import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { Button } from '../../../../components/Button/Button'

import { PreciseFPSettingsModal } from './PreciseFPSettingsModal'

import { getIntegrationRedirectUri } from '../../../Integration/utils'

import '../IntegrationsTab.scss'
import { IntegrationIcon } from '../../../../components/IntegrationIcon'

export type PreciseFPIntegrationRowProps = { useIntegrationRedirectUri?: boolean }

export const PreciseFPIntegrationRow: React.FC<PreciseFPIntegrationRowProps> = ({ useIntegrationRedirectUri }) => {
  const accountId = useAppSelector(selectAccountId)

  const [showSettingsModal, setShowSettingsModal] = useState(false)

  const [deleteUserIntegration, { isLoading: isDeletingIntegration }] = useDeleteUserIntegrationMutation()

  const { data: currentUser } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })

  const hasPreciseFPIntegration = useMemo(() => {
    if (!currentUser?.integrations) {
      return false
    }
    return currentUser.integrations.some((i) => i.serviceId === IntegrationServiceId.PRECISE_FP)
  }, [currentUser])

  const authorizePreciseFP = () => {
    const redirectUri = getIntegrationRedirectUri(IntegrationServiceId.PRECISE_FP)
    const state = useIntegrationRedirectUri ? createState(window.location.origin + '/dashboard/account/profile-settings/integrations') : createState()
    window.location.href = `${import.meta.env.REACT_APP_IAM_API_BASE_URL}/accounts/${accountId}/user-integrations/${IntegrationServiceId.PRECISE_FP}/enable?redirect_uri=${redirectUri}&state=${state}`
  }

  const uninstallPreciseFP = () => {
    if (!currentUser?.integrations) {
      return
    }
    const integration = currentUser.integrations.find((i) => i.serviceId === IntegrationServiceId.PRECISE_FP)
    deleteUserIntegration({
      accountId,
      payload: {
        userId: currentUser.id,
        integrationId: integration!.id,
      },
    })
  }

  const handleShowSettingsModal = async () => {
    setShowSettingsModal(true)
  }

  const handleHideSettings = () => {
    setShowSettingsModal(false)
  }

  return (
    <Container className='p-3 p-md-0 py-md-3 integration-container'>
      {showSettingsModal && <PreciseFPSettingsModal show={showSettingsModal} onHide={handleHideSettings} />}
      <Row className='d-flex justify-content-between'>
        <Col>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <div className='rounded py-1 px-1 zocks-bg-color-gray-100'>
              <IntegrationIcon serviceId={IntegrationServiceId.PRECISE_FP} width={48} height={48} />
            </div>
            <div>
              <h6>PreciseFP</h6>
              <div>Connect your Zocks account to PreciseFP.</div>
            </div>
          </Stack>
        </Col>
        <Col md='auto'>
          <Stack direction='horizontal' className='gap-3 align-items-start'>
            {hasPreciseFPIntegration ? (
              <div className='d-flex gap-3'>
                <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'account:read', superScope: ['account:admin'] }}>
                  <Button variant='primaryGhost' onClick={handleShowSettingsModal}>
                    <i className='bi bi-gear' />
                  </Button>
                </ScopedBlock>
              </div>
            ) : null}
            <div className='connect-button-container'>
              {hasPreciseFPIntegration ? (
                <div className='connect-button-container'>
                  <Button variant='primary' onClick={uninstallPreciseFP} loading={isDeletingIntegration}>
                    Disconnect
                  </Button>
                  <div className='pt-2 d-flex justify-content-end zocks-text-success'>
                    <i className='bi bi-check' />
                    Connected
                  </div>
                </div>
              ) : (
                <ScopedBlock
                  scope={{
                    scope: 'integration:connect',
                    superScope: ['integration:admin'],
                  }}
                  missingScopeAction={MissingScopeAction.DISABLE}
                >
                  <Button variant='primary' onClick={authorizePreciseFP}>
                    Connect
                  </Button>
                </ScopedBlock>
              )}
            </div>
          </Stack>
        </Col>
      </Row>
    </Container>
  )
}
